// The reward chip inside the "Review" CTA ("Upto +Npts").
//
// It's the part of that button that actually motivates the click — the points
// on offer — but it rendered as flat furniture. This gives it a slow heartbeat:
// a warm gold flush, a small lift, and a sheen sweep, on a long cycle so it
// invites attention without nagging.
//
// Behaviour only: the pill's own look still comes from the `style` passed by
// the call site (styles.reviewGameActionCoinsContainer), so there's one source
// of truth for the visual.

import React, {useEffect} from 'react';
import {StyleProp, StyleSheet, ViewStyle} from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import Shine from '@/Atoms/Shine';

interface CoinsChipProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Colour of the flush that washes over the chip on each beat. */
  tint?: string;
  /** Stagger the cycle if more than one chip is ever on screen. */
  delay?: number;
}

export default function CoinsChip({
  children,
  style,
  tint = '#FFD87A',
  delay = 0,
}: CoinsChipProps) {
  const beat = useSharedValue(0);

  useEffect(() => {
    beat.value = withRepeat(
      withSequence(
        // Long rest between beats — this sits next to a Shine sweep and a
        // hovering button, and a fast pulse here would turn the CTA into noise.
        withTiming(0, {duration: 2400 + delay}),
        withTiming(1, {duration: 420, easing: Easing.out(Easing.quad)}),
        withTiming(0, {duration: 620, easing: Easing.inOut(Easing.quad)}),
      ),
      -1,
      false,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chipStyle = useAnimatedStyle(() => ({
    transform: [{translateY: beat.value * -2}, {scale: 1 + beat.value * 0.04}],
  }));

  const tintStyle = useAnimatedStyle(() => ({
    opacity: beat.value * 0.55,
  }));

  return (
    <Animated.View style={[style, styles.clip, chipStyle]}>
      {/* Absolutely positioned, so it tints the pill without touching the row
          layout, and paints under the icon/label because it comes first. */}
      <Animated.View
        pointerEvents="none"
        style={[StyleSheet.absoluteFill, {backgroundColor: tint}, tintStyle]}
      />
      <Shine travel={150} width={26} interval={3000} delay={600} />
      {children}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  clip: {
    // Required so the tint and the sheen are clipped to the pill's radius.
    position: 'relative',
    overflow: 'hidden',
  },
});
